/**
 * Dead letters: los mensajes que agotaron sus reintentos.
 *
 * Nada muere en silencio — todo lo que se rindio termina aca, con su historial
 * congelado, y de aca sale reprocesado o descartado **con motivo**. El reproceso
 * y el descarte son acciones de operacion: se muestran solo a quien tiene el
 * permiso (`operator` en adelante). La autorizacion real la vuelve a hacer la
 * API; esconder el boton es cortesia, no seguridad.
 */
import { Fragment, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import type { DeadLetterRow, IncidentFilters, PageState } from '../api/types';
import { useMonitorApi } from '../app/api-context';
import { useCan } from '../auth/auth-context';
import { ErrorBanner } from '../components/ErrorBanner';
import { EventName } from '../components/EventName';
import { EmptyState, LoadingBlock } from '../components/Feedback';
import { JsonPanel } from '../components/JsonPanel';
import { Pagination } from '../components/Pagination';
import { ReasonDialog } from '../components/ReasonDialog';
import { StatusBadge } from '../components/StatusBadge';
import { SelectFilter } from '../components/SelectFilter';
import { useAsyncResource } from '../hooks/use-async-resource';
import { FLOW_OPTIONS, SYSTEM_OPTIONS, resolutionLabel, systemLabel } from '../lib/catalogs';
import type { Option } from '../lib/catalogs';
import { DEFAULT_PAGE_SIZE, EMPTY_INCIDENT_FILTERS } from '../lib/filters';
import { formatDateTime, shortId } from '../lib/format';

const RESOLUTION_OPTIONS: readonly Option<'pending' | 'resolved'>[] = [
  { value: 'pending', label: 'Sin resolver' },
  { value: 'resolved', label: 'Resueltas' },
];

export function DeadLettersPage(): ReactElement {
  const api = useMonitorApi();
  const canReplay = useCan('deadletters:replay');
  const canDiscard = useCan('deadletters:discard');

  const [filters, setFilters] = useState<IncidentFilters>({
    ...EMPTY_INCIDENT_FILTERS,
    resolution: 'pending',
  });
  const [page, setPage] = useState<PageState>({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [discarding, setDiscarding] = useState<DeadLetterRow | null>(null);

  const queryKey = useMemo(() => JSON.stringify({ filters, page }), [filters, page]);
  const resource = useAsyncResource(() => api.listDeadLetters(filters, page), [api, queryKey]);

  const rows = resource.data?.items ?? [];

  const replay = (row: DeadLetterRow): void => {
    setBusyId(row.deadLetter.id);
    setActionError(null);
    setNotice(null);
    void api.replayDeadLetter(row.deadLetter.id).then(
      () => {
        setBusyId(null);
        setNotice(`Se reencoló el mensaje ${shortId(row.deadLetter.messageId)}.`);
        resource.reload();
      },
      (error: unknown) => {
        setBusyId(null);
        setActionError(error);
      },
    );
  };

  const discard = (row: DeadLetterRow, reason: string): void => {
    setBusyId(row.deadLetter.id);
    setActionError(null);
    setNotice(null);
    void api.discardDeadLetter(row.deadLetter.id, reason).then(
      () => {
        setBusyId(null);
        setDiscarding(null);
        setNotice(`Se descartó la dead letter ${shortId(row.deadLetter.id)}.`);
        resource.reload();
      },
      (error: unknown) => {
        setBusyId(null);
        setActionError(error);
      },
    );
  };

  const changeFilters = (next: IncidentFilters): void => {
    setFilters(next);
    setPage((previous) => ({ ...previous, page: 1 }));
  };

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1>Dead letters</h1>
          <p className="page__subtitle">
            Mensajes que agotaron sus reintentos. Reprocesar vuelve a encolar el mensaje original;
            descartar lo cierra sin ejecutarlo y exige un motivo, que queda guardado en la dead
            letter.
          </p>
        </div>
        <button type="button" className="button" onClick={resource.reload}>
          Actualizar
        </button>
      </header>

      <section className="panel">
        <div className="panel__body">
          <div className="filters">
            <SelectFilter
              label="Sistema"
              value={filters.system}
              options={SYSTEM_OPTIONS}
              onChange={(value) => changeFilters({ ...filters, system: value })}
            />
            <SelectFilter
              label="Flujo"
              value={filters.flow}
              options={FLOW_OPTIONS}
              onChange={(value) => changeFilters({ ...filters, flow: value })}
            />
            <SelectFilter
              label="Estado"
              value={filters.resolution}
              options={RESOLUTION_OPTIONS}
              allLabel="Todas"
              onChange={(value) => changeFilters({ ...filters, resolution: value })}
            />
          </div>
        </div>
      </section>

      {notice === null ? null : (
        <div className="banner banner--info" role="status">
          {notice}
        </div>
      )}
      <ErrorBanner error={actionError} />
      <ErrorBanner error={resource.error} onRetry={resource.reload} />

      <section className="panel">
        <header className="panel__header">
          <h2>Pendientes y resueltas</h2>
        </header>

        {resource.loading && resource.data === null ? (
          <LoadingBlock />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No hay dead letters con estos filtros."
            hint="Es la situación sana: ningún mensaje agotó sus reintentos."
          />
        ) : (
          <div className="table__wrap">
            <table className="table table--stack">
              <thead>
                <tr>
                  <th scope="col">Creada</th>
                  <th scope="col">Sistema</th>
                  <th scope="col">Flujo</th>
                  <th scope="col">Evento</th>
                  <th scope="col">Motivo</th>
                  <th scope="col">Estado</th>
                  <th scope="col">Mensaje</th>
                  <th scope="col">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const { deadLetter, message } = row;
                  const expanded = expandedId === deadLetter.id;
                  const resolved = deadLetter.resolvedAt !== null;
                  return (
                    <Fragment key={deadLetter.id}>
                      <tr>
                        <td className="nowrap" data-label="Creada">
                          {formatDateTime(deadLetter.createdAt)}
                        </td>
                        <td className="nowrap" data-label="Sistema">
                          {message === null ? '—' : systemLabel(message.system)}
                        </td>
                        <td className="nowrap" data-label="Flujo">
                          {message?.flow ?? '—'}
                        </td>
                        <td className="mono table__lead">
                          <EventName value={message?.eventType ?? null} />
                        </td>
                        <td className="table__error table__cell--wide" data-label="Motivo">
                          <span className="truncate" title={deadLetter.reason}>
                            {deadLetter.reason}
                          </span>
                        </td>
                        <td className="nowrap table__pill">
                          {resolved ? (
                            <span className="badge badge--processed">
                              {resolutionLabel(deadLetter.resolution)}
                            </span>
                          ) : (
                            <span className="badge badge--dead">Sin resolver</span>
                          )}
                        </td>
                        <td data-label="Mensaje">
                          {message === null ? (
                            <span className="muted mono">{shortId(deadLetter.messageId)}</span>
                          ) : (
                            <span className="row">
                              <Link className="mono" to={`/mensajes/${deadLetter.messageId}`}>
                                {shortId(deadLetter.messageId)}
                              </Link>
                              {/*
                                Mientras sigue muerto, "Dead letter" repite lo que dice la
                                pantalla entera. Se muestra cuando cambió (un reproceso que
                                terminó procesado o volvió a fallar), que es cuando informa.
                              */}
                              {message.status === 'dead' ? null : (
                                <StatusBadge status={message.status} />
                              )}
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="table__actions">
                            <button
                              type="button"
                              className="button button--sm button--ghost"
                              onClick={() => setExpandedId(expanded ? null : deadLetter.id)}
                            >
                              {expanded ? 'Ocultar' : 'Historial'}
                            </button>
                            {canReplay && !resolved ? (
                              <button
                                type="button"
                                className="button button--sm button--primary"
                                disabled={busyId === deadLetter.id}
                                onClick={() => replay(row)}
                              >
                                Reprocesar
                              </button>
                            ) : null}
                            {canDiscard && !resolved ? (
                              <button
                                type="button"
                                className="button button--sm button--danger"
                                disabled={busyId === deadLetter.id}
                                onClick={() => {
                                  setActionError(null);
                                  setDiscarding(row);
                                }}
                              >
                                Descartar
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                      {expanded ? (
                        <tr>
                          <td colSpan={8}>
                            <div className="stack">
                              <JsonPanel
                                title="Historial de intentos congelado"
                                value={deadLetter.history}
                                emptyLabel="La dead letter no guardó historial."
                              />
                              {deadLetter.resolvedBy === null ? null : (
                                <p className="muted">
                                  Resuelta por {deadLetter.resolvedBy} el{' '}
                                  {formatDateTime(deadLetter.resolvedAt)}.
                                </p>
                              )}
                              {/*
                                Una fila cerrada sin nota tiene que DECIRLO. Si no
                                se renderiza nada, un descarte anterior a la
                                migracion se ve igual que uno donde la nota se
                                perdio, que es justo la duda que esto vino a sacar.
                              */}
                              {deadLetter.resolution !== 'discarded' ? null : (
                                <p className="muted">
                                  <strong>Motivo del descarte:</strong>{' '}
                                  {deadLetter.resolutionNote ?? 'sin registrar (cierre anterior)'}
                                </p>
                              )}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          page={page.page}
          pageSize={page.pageSize}
          total={resource.data?.total ?? 0}
          onPageChange={(nextPage) => setPage((previous) => ({ ...previous, page: nextPage }))}
          onPageSizeChange={(pageSize) => setPage({ page: 1, pageSize })}
        />
      </section>

      {discarding === null ? null : (
        <ReasonDialog
          title="Descartar la dead letter"
          description="El mensaje no se vuelve a ejecutar. El motivo queda guardado en la dead letter, con tu correo, y se ve al abrirla."
          fieldLabel="Motivo del descarte"
          confirmLabel="Descartar"
          busy={busyId === discarding.deadLetter.id}
          error={actionError}
          onCancel={() => setDiscarding(null)}
          onConfirm={(reason) => discard(discarding, reason)}
        />
      )}
    </div>
  );
}
